import * as THREE from 'three';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import proj4 from 'proj4';
import { ENUToGeodetic } from './geodetic';
import type { PinObserver } from '../types/scenePins';

/** SunCalc 2: degrees clockwise from north. This viewer is X=north,Y=up,Z=east.
 * Radius is a schematic display radius, not the physical distance to the sun. */
export function sunAnglesToWorld(azimuth: number, altitude: number, origin: WorldPos, radius: number): WorldPos {
  const az = THREE.MathUtils.degToRad(azimuth), alt = THREE.MathUtils.degToRad(altitude);
  return { x: origin.x + radius * Math.cos(alt) * Math.cos(az),
    y: origin.y + radius * Math.sin(alt), z: origin.z + radius * Math.cos(alt) * Math.sin(az) };
}

/** Schematic beam width across the house, perpendicular to the solar bearing. */
export function sunBeamBase(azimuth: number, origin: WorldPos): WorldPos[] {
  const az = THREE.MathUtils.degToRad(azimuth);
  return [-1, 1].map(side => ({ x: origin.x - side * 5 * Math.sin(az),
    y: origin.y, z: origin.z + side * 5 * Math.cos(az) }));
}

export function sunHorizonRing(origin: WorldPos, radius: number): WorldPos[] {
  return Array.from({ length: 97 }, (_, i) => sunAnglesToWorld(i * 360 / 96, 0, origin, radius));
}

/** Steep, close aerial framing keeps the 24 m sun dome over reconstructed ground. */
export function sunPathCameraView(origin: WorldPos, aspect = 1.2, leftInset = 0): { position: [number, number, number]; lookAt: [number, number, number] } {
  const inset = THREE.MathUtils.clamp(leftInset, 0, 0.45);
  const scale = Math.max(1, 1.2 / Math.max(aspect * (1 - inset), 0.5));
  // Moving camera and target east shifts the house and dial right in this north-facing view.
  const distance = Math.hypot(18 * scale, 48 * scale - 1);
  const eastOffset = distance * Math.tan(THREE.MathUtils.degToRad(30)) * aspect * inset;
  return { position: [origin.x + 18 * scale, origin.y + 48 * scale, origin.z + eastOffset],
    lookAt: [origin.x, origin.y + 1, origin.z + eastOffset] };
}

/** Break projected paths at the near/far clipping planes rather than drawing across the camera. */
export function projectWorldPath(points: WorldPos[], camera: THREE.Camera, canvas: HTMLCanvasElement): string {
  const rect = canvas.getBoundingClientRect();
  const vector = new THREE.Vector3();
  let path = '', connected = false;
  for (const p of points) {
    vector.set(p.x, p.y, p.z).project(camera);
    if (vector.z < -1 || vector.z > 1) { connected = false; continue; }
    const x = rect.left + (vector.x + 1) * canvas.clientWidth / 2;
    const y = rect.top + (1 - vector.y) * canvas.clientHeight / 2;
    path += `${connected ? 'L' : 'M'} ${x.toFixed(2)} ${y.toFixed(2)} `;
    connected = true;
  }
  return path;
}

/** US real-estate display units; reconstruction calculations remain in metres. */
export function formatFeet(metres: number, decimals = 1): string {
  return `${(metres / 0.3048).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ft`;
}

export function formatSquareFeet(squareMetres: number): string {
  return `${Math.round(squareMetres / 0.09290304).toLocaleString('en-US')} sq ft`;
}

export function formatAcres(squareMetres: number): string {
  return `${(squareMetres / 4046.8564224).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} acres`;
}

export function formatAcresFromSquareFeet(squareFeet: number): string {
  return `${(squareFeet / 43560).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} acres`;
}

/** Plan-view parcel measurements; the source polygon need not be rectangular. */
export function describeParcelRing(ring: [number, number, number][]) {
  const points = ring.map(([x, y, z]) => ({ x, y, z }));
  const center = points.reduce((sum, p) => ({ x: sum.x + p.x / points.length,
    y: sum.y + p.y / points.length, z: sum.z + p.z / points.length }), { x: 0, y: 0, z: 0 });
  const edges = points.map((p, i) => {
    const next = points[(i + 1) % points.length];
    return { midpoint: { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2, z: (p.z + next.z) / 2 },
      length: worldDistanceXZ(p, next) };
  });
  const area = polygonAreaXZ(points.map(worldPos => ({ worldPos, latitude: 0, longitude: 0, altitude: worldPos.y })));
  return { points, center, edges, area };
}

/** Narrow oriented selection boxes along the parcel edges, limited to ground and fences. */
export function parcelBoundaryStrips(ring: [number, number, number][], width = 0.4, height = 4) {
  return ring.map((point, index) => {
    const next = ring[(index + 1) % ring.length];
    const dx = next[0] - point[0], dz = next[2] - point[2];
    return {
      position: [(point[0] + next[0]) / 2, (point[1] + next[1]) / 2, (point[2] + next[2]) / 2] as [number, number, number],
      scale: [Math.hypot(dx, dz) / 2, height / 2, width / 2] as [number, number, number],
      rotationY: -Math.atan2(dz, dx),
    };
  });
}

/** Project the complete boundary only when its vertices are in front of the camera. */
export function projectParcelRing(points: WorldPos[], camera: THREE.Camera, canvas: HTMLCanvasElement) {
  const vector = new THREE.Vector3();
  if (points.some(p => {
    vector.set(p.x, p.y, p.z).project(camera);
    return vector.z < -1 || vector.z > 1;
  })) return null;
  return points.map(p => projectToScreen(p, camera, canvas));
}

export interface WorldPos {
  x: number;
  y: number;
  z: number;
}

export interface GeoPoint {
  latitude: number;
  longitude: number;
  altitude: number;
  worldPos: WorldPos;
  coordinateMode?: 'geodetic' | 'world';
}

export interface ScreenPos {
  x: number;
  y: number;
  visible: boolean;
}

export interface LineMetrics {
  rise: number;
  run: number;      // horizontal ground-plane distance
  length: number;   // true 3D distance
  angle: number;    // slope angle in degrees
  grade: number | null;
}

export interface ProjectionResultXZ {
  projectedPoint: WorldPos;
  t: number;                  // parametric position on AB (0=start, 1=end if clamped)
  perpendicularDistance: number;
  alongDistance: number;      // distance from A to projected point
  isClamped: boolean;
}

export interface WidthMetrics {
  width: number;              // perpendicular width in XZ plane
  //skewDistance: number;       // raw picked distance in XZ plane
  projectedPoint: WorldPos;   // foot of perpendicular on baseline
  t: number;
  alongDistance: number;
  isClamped: boolean;
}

export interface WorldVector3D {
  x: number;
  y: number;
  z: number;
}

export interface WorldVectorXZ {
  x: number;
  z: number;
}

// EPSG:2950-style local projection used in your current app
proj4.defs(
  'FLAWED_MTM8',
  '+proj=tmerc +lat_0=0 +lon_0=-73.5 +k=0.9999 +x_0=304800 +y_0=0 +ellps=GRS80 +units=m +no_defs'
);

//proj4.defs( "EPSG:2950", "+proj=tmerc +lat_0=0 +lon_0=-73.5 +k=0.9999 +x_0=304800 +y_0=0 +ellps=GRS80 +nadgrids=@ntv2_0.gsb,null +units=m +no_defs" );
//const WGS84_TO_MTM8 = proj4('WGS84', 'EPSG:2950');

const WGS84_TO_MTM8 = proj4('WGS84', 'FLAWED_MTM8');

const MTM8_TO_WGS84 = proj4('FLAWED_MTM8', 'WGS84');

const EPS = 1e-9;

/* ------------------------------------------------------------------ */
/* Coordinate conversion utilities                                    */
/* ------------------------------------------------------------------ */

export function worldToGeodetic(
  worldVec: WorldPos,
  observerCoordinates: [number, number, number]
): { latitude: number; longitude: number; altitude: number } {
  const qx = 0.5, qy = 0.5, qz = 0.5, qw = -0.5;
  const iqx = -qx, iqy = -qy, iqz = -qz, iqw = qw;
  const { x, y, z } = worldVec;

  const tx = 2 * (iqy * z - iqz * y);
  const ty = 2 * (iqz * x - iqx * z);
  const tz = 2 * (iqx * y - iqy * x);

  const enu = {
    x: x + iqw * tx + iqy * tz - iqz * ty,
    y: y + iqw * ty + iqz * tx - iqx * tz,
    z: z + iqw * tz + iqx * ty - iqy * tx,
  };

  const [obsLat, obsLon, obsAlt] = observerCoordinates;
  const { lat, lon, alt } = ENUToGeodetic(enu.x, enu.y, enu.z, obsLat, obsLon, obsAlt);

  return {
    latitude: lat,
    longitude: lon,
    altitude: alt,
  };
}

export function latLonToMTM8(lat: number, lon: number): { easting: number; northing: number } {
  const [easting, northing] = WGS84_TO_MTM8.forward([lon, lat]);
  return { easting, northing };
}

export function mtm8ToLatLon(
  easting: number,
  northing: number
): { latitude: number; longitude: number } {
  const [lon, lat] = MTM8_TO_WGS84.forward([easting, northing]);
  return { latitude: lat, longitude: lon };
}

export function mtm8ToGeodetic(
  x: number,
  y: number,
  z: number
): { latitude: number; longitude: number; altitude: number } {
  const { latitude, longitude } = mtm8ToLatLon(x, y);
  return {
    latitude,
    longitude,
    altitude: z,
  };
}

/* ------------------------------------------------------------------ */
/* Screen projection                                                   */
/* ------------------------------------------------------------------ */

export function projectToScreen(
  worldPos: WorldPos,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement
): ScreenPos {
  const vec = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
  vec.project(camera);

  return {
    x: (vec.x * 0.5 + 0.5) * canvas.clientWidth,
    y: (-vec.y * 0.5 + 0.5) * canvas.clientHeight,
    visible:
      vec.x >= -1 &&
      vec.x <= 1 &&
      vec.y >= -1 &&
      vec.y <= 1 &&
      vec.z >= -1 &&
      vec.z <= 1,
  };
}

export function screenToWorldOnPlane(
  clientX: number,
  clientY: number,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  planeY: number
): WorldPos | null {
  const rect = canvas.getBoundingClientRect();

  const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
  const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;

  const origin = new THREE.Vector3();
  origin.setFromMatrixPosition(camera.matrixWorld);

  const point = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(camera);
  const direction = point.sub(origin).normalize();

  // Ray is almost parallel to the horizontal plane
  if (Math.abs(direction.y) < EPS) return null;

  const t = (planeY - origin.y) / direction.y;
  if (t < 0) return null;

  const hit = origin.clone().add(direction.multiplyScalar(t));

  return {
    x: hit.x,
    y: planeY,
    z: hit.z,
  };
}

export function cameraForward3D(camera: THREE.Camera): WorldVector3D | null {
  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);

  return normalize3D({
    x: forward.x,
    y: forward.y,
    z: forward.z,
  });
}

/* ------------------------------------------------------------------ */
/* Basic world-space geometry helpers                                  */
/* ------------------------------------------------------------------ */

export function worldDistance3D(a: WorldPos, b: WorldPos): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function worldDistanceXZ(a: WorldPos, b: WorldPos): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dz * dz);
}

export function verticalDifference(a: WorldPos, b: WorldPos): number {
  return Math.abs(b.y - a.y);
}

export function vectorXZ(a: WorldPos, b: WorldPos): WorldVectorXZ {
  return {
    x: b.x - a.x,
    z: b.z - a.z,
  };
}

export function vectorXZLength(v: WorldVectorXZ): number {
  return Math.sqrt(v.x * v.x + v.z * v.z);
}

export function normalizeXZ(v: WorldVectorXZ): WorldVectorXZ | null {
  const len = vectorXZLength(v);
  if (len < EPS) return null;
  return { x: v.x / len, z: v.z / len };
}

export function dotXZ(a: WorldVectorXZ, b: WorldVectorXZ): number {
  return a.x * b.x + a.z * b.z;
}

export function crossXZ(a: WorldVectorXZ, b: WorldVectorXZ): number {
  // scalar z-component equivalent in 2D XZ-plane
  return a.x * b.z - a.z * b.x;
}

export function perpendicularXZ(v: WorldVectorXZ): WorldVectorXZ {
  return { x: -v.z, z: v.x };
}

/* ------------------------------------------------------------------ */
/* Geodetic distance helpers                                           */
/* ------------------------------------------------------------------ */

export function segmentDistance(a: GeoPoint, b: GeoPoint): number {
  const midLatRad = ((a.latitude + b.latitude) / 2) * Math.PI / 180;
  const R = 6371000 / Math.sqrt(1 - 0.00669437999014 * Math.sin(midLatRad) * Math.sin(midLatRad));
  const phi1 = (a.latitude * Math.PI) / 180;
  const phi2 = (b.latitude * Math.PI) / 180;
  const deltaPhi = ((b.latitude - a.latitude) * Math.PI) / 180;
  const deltaLambda = ((b.longitude - a.longitude) * Math.PI) / 180;

  const sinA =
    Math.sin(deltaPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;

  const horiz = R * 2 * Math.atan2(Math.sqrt(sinA), Math.sqrt(1 - sinA));
  const vert = b.altitude - a.altitude;

  return Math.sqrt(horiz ** 2 + vert ** 2);
}

export function totalLength(pts: GeoPoint[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += segmentDistance(pts[i - 1], pts[i]);
  return d;
}

/* ------------------------------------------------------------------ */
/* World-space measurement helpers                                     */
/* ------------------------------------------------------------------ */

export function totalLengthWorld3D(pts: GeoPoint[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) {
    d += worldDistance3D(pts[i - 1].worldPos, pts[i].worldPos);
  }
  return d;
}

export function totalLengthWorldXZ(pts: GeoPoint[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) {
    d += worldDistanceXZ(pts[i - 1].worldPos, pts[i].worldPos);
  }
  return d;
}

export function lineMetricsWorld(a: GeoPoint, b: GeoPoint): LineMetrics {
  const dx = b.worldPos.x - a.worldPos.x;
  const dy = b.worldPos.y - a.worldPos.y;
  const dz = b.worldPos.z - a.worldPos.z;

  const rise = Math.abs(dy);
  const run = Math.sqrt(dx * dx + dz * dz);
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const angle = run > 0.01 ? Math.atan2(rise, run) * (180 / Math.PI) : 90;
  const grade = run > 0.01 ? (rise / run) * 100 : null;

  return { rise, run, length, angle, grade };
}

/* ------------------------------------------------------------------ */
/* Projection / perpendicular width helpers in XZ plane                */
/* ------------------------------------------------------------------ */

export function projectPointToLineXZ(
  point: WorldPos,
  lineStart: WorldPos,
  lineEnd: WorldPos,
  clampToSegment = true
): ProjectionResultXZ | null {
  const ab = vectorXZ(lineStart, lineEnd);
  const ap = vectorXZ(lineStart, point);

  const abLenSq = ab.x * ab.x + ab.z * ab.z;
  if (abLenSq < EPS) return null;

  const rawT = dotXZ(ap, ab) / abLenSq;
  const t = clampToSegment ? Math.max(0, Math.min(1, rawT)) : rawT;
  const isClamped = clampToSegment && Math.abs(t - rawT) > EPS;

  const projectedPoint: WorldPos = {
    x: lineStart.x + ab.x * t,
    y: point.y, // keep caller's Y unless they want to replace it later
    z: lineStart.z + ab.z * t,
  };

  const perpendicularDistance = worldDistanceXZ(point, projectedPoint);
  const alongDistance = vectorXZLength(ab) * t;

  return {
    projectedPoint,
    t,
    perpendicularDistance,
    alongDistance,
    isClamped,
  };
}

export function perpendicularWidthToBaselineXZ(
  baselineStart: GeoPoint,
  baselineEnd: GeoPoint,
  targetPoint: GeoPoint,
  clampToSegment = true
): WidthMetrics | null {
  const projection = projectPointToLineXZ(
    targetPoint.worldPos,
    baselineStart.worldPos,
    baselineEnd.worldPos,
    clampToSegment
  );

  if (!projection) return null;

  return {
  width: projection.perpendicularDistance,
  projectedPoint: projection.projectedPoint,
  t: projection.t,
  alongDistance: projection.alongDistance,
  isClamped: projection.isClamped,
};
}

export function baselineDirectionXZ(
  start: GeoPoint,
  end: GeoPoint
): WorldVectorXZ | null {
  return normalizeXZ(vectorXZ(start.worldPos, end.worldPos));
}

export function pointOffsetAlongAndAcrossBaselineXZ(
  baselineStart: GeoPoint,
  baselineEnd: GeoPoint,
  point: GeoPoint,
  clampToSegment = false
): { along: number; across: number; projectedPoint: WorldPos; t: number } | null {
  const projection = projectPointToLineXZ(
    point.worldPos,
    baselineStart.worldPos,
    baselineEnd.worldPos,
    clampToSegment
  );

  if (!projection) return null;

  const ab = vectorXZ(baselineStart.worldPos, baselineEnd.worldPos);
  const ap = vectorXZ(baselineStart.worldPos, point.worldPos);
  const sign = Math.sign(crossXZ(ab, ap)) || 1;

  return {
    along: projection.alongDistance,
    across: projection.perpendicularDistance * sign,
    projectedPoint: projection.projectedPoint,
    t: projection.t,
  };
}

/* ------------------------------------------------------------------ */
/* Polygon helpers                                                     */
/* ------------------------------------------------------------------ */

export function polygonArea(pts: GeoPoint[]): number {
  if (pts.length < 3) return 0;

  const origin = new THREE.Vector3(
    pts[0].worldPos.x,
    pts[0].worldPos.y,
    pts[0].worldPos.z
  );

  const areaVector = new THREE.Vector3(0, 0, 0);

  for (let i = 1; i < pts.length - 1; i++) {
    const a = new THREE.Vector3(
      pts[i].worldPos.x,
      pts[i].worldPos.y,
      pts[i].worldPos.z
    ).sub(origin);

    const b = new THREE.Vector3(
      pts[i + 1].worldPos.x,
      pts[i + 1].worldPos.y,
      pts[i + 1].worldPos.z
    ).sub(origin);

    areaVector.add(a.clone().cross(b));
  }

  return 0.5 * areaVector.length();
}

export function polygonAreaXZ(pts: GeoPoint[]): number {
  if (pts.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i].worldPos;
    const b = pts[(i + 1) % pts.length].worldPos;
    area += a.x * b.z - b.x * a.z;
  }

  return Math.abs(area) * 0.5;
}

/* ------------------------------------------------------------------ */
/* Formatters                                                          */
/* ------------------------------------------------------------------ */

export function fmtDist(m: number): string {
  return m < 1000 ? `${m.toFixed(1)} m` : `${(m / 1000).toFixed(3)} km`;
}

export function fmtArea(m2: number): string {
  return m2 < 10000 ? `${m2.toFixed(1)} m²` : `${(m2 / 10000).toFixed(3)} ha`;
}

export function fmtUTM(v: number): string {
  return v.toFixed(2);
}

/* ------------------------------------------------------------------ */
/* Marker helpers                                                      */
/* ------------------------------------------------------------------ */

export const MARKER_COLORS = [
  '#22C55E',
  '#EF4444',
  '#3B82F6',
  '#F59E0B',
  '#A855F7',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
];

export function markerColor(i: number): string {
  return MARKER_COLORS[i % MARKER_COLORS.length];
}

export const LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

export function markerLabel(i: number): string {
  return LABELS[i] ?? `${i + 1}`;
}

/* ------------------------------------------------------------------ */
/* Relative point movement helpers                                    */
/* ------------------------------------------------------------------ */

export function cameraForwardXZ(camera: THREE.Camera): WorldVectorXZ | null {
  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);
  return normalizeXZ({ x: forward.x, z: forward.z });
}

export function signedDistanceAlongXZ(
  origin: WorldPos,
  point: WorldPos,
  dir: WorldVectorXZ
): number {
  return (point.x - origin.x) * dir.x + (point.z - origin.z) * dir.z;
}

export function closestPointOnInfiniteLineXZ(
  point: WorldPos,
  linePoint: WorldPos,
  lineDir: WorldVectorXZ
): WorldPos {
  const t =
    (point.x - linePoint.x) * lineDir.x +
    (point.z - linePoint.z) * lineDir.z;

  return {
    x: linePoint.x + t * lineDir.x,
    y: linePoint.y,
    z: linePoint.z + t * lineDir.z,
  };
}

export function projectPointToDirectionXZ(
  point: WorldPos,
  linePoint: WorldPos,
  dir: WorldVectorXZ
): { projectedPoint: WorldPos; signedDistance: number } {
  const signedDistance =
    (point.x - linePoint.x) * dir.x +
    (point.z - linePoint.z) * dir.z;

  return {
    signedDistance,
    projectedPoint: {
      x: linePoint.x + signedDistance * dir.x,
      y: linePoint.y,
      z: linePoint.z + signedDistance * dir.z,
    },
  };
}

/* ------------------------------------------------------------------ */
/* Additional 3D vector helpers                     */
/* ------------------------------------------------------------------ */

export interface BBox3D {
  min: [number, number, number];
  max: [number, number, number];
}

export interface OrientedSelectionBounds {
  center: WorldPos;
  size: WorldPos;
  quaternion: [number, number, number, number];
}

export interface PeekSelectionVolume {
  center: WorldPos;
  halfSize: WorldPos;
  axisX: WorldPos;
  axisY: WorldPos;
  axisZ: WorldPos;
}

/** Projects a normalized screen-space selection onto the detected selection
 * plane, preserving the rectangle/lasso's drawn extent instead of shrinking it
 * to the subset of pixels that produced splat hits. */
export function projectScreenSelectionToPlane(
  bounds: OrientedSelectionBounds,
  screenPoints: Array<{ x: number; y: number }>,
  camera: THREE.Camera,
): OrientedSelectionBounds | null {
  if (screenPoints.length < 3) return null;

  camera.updateMatrixWorld(true);
  const quaternion = new THREE.Quaternion(...bounds.quaternion).normalize();
  const axisX = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
  const axisY = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion);
  const planeNormal = new THREE.Vector3(0, 0, -1).applyQuaternion(quaternion);
  const planeCenter = new THREE.Vector3(bounds.center.x, bounds.center.y, bounds.center.z);
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, planeCenter);
  const rayOrigin = new THREE.Vector3().setFromMatrixPosition(camera.matrixWorld);
  const projected = screenPoints.flatMap(point => {
    const worldPoint = new THREE.Vector3(point.x * 2 - 1, 1 - point.y * 2, 0.5).unproject(camera);
    const ray = new THREE.Ray(rayOrigin, worldPoint.sub(rayOrigin).normalize());
    const hit = ray.intersectPlane(plane, new THREE.Vector3());
    return hit ? [hit] : [];
  });
  if (projected.length < 3) return null;

  const localX = projected.map(point => point.clone().sub(planeCenter).dot(axisX));
  const localY = projected.map(point => point.clone().sub(planeCenter).dot(axisY));
  const minX = Math.min(...localX);
  const maxX = Math.max(...localX);
  const minY = Math.min(...localY);
  const maxY = Math.max(...localY);
  const center = planeCenter.clone()
    .addScaledVector(axisX, (minX + maxX) * 0.5)
    .addScaledVector(axisY, (minY + maxY) * 0.5);

  return {
    center: { x: center.x, y: center.y, z: center.z },
    size: {
      x: Math.max(maxX - minX, 0.1),
      y: Math.max(maxY - minY, 0.1),
      z: 0.02,
    },
    quaternion: bounds.quaternion,
  };
}

/** Builds a camera-aligned prism beginning just in front of a selected surface
 * and extending behind it. The returned axes and dimensions are world-space. */
export function buildPeekSelectionVolume(
  bounds: OrientedSelectionBounds,
  depth = 3,
  frontPadding = 0.15,
): PeekSelectionVolume {
  const quaternion = new THREE.Quaternion(...bounds.quaternion).normalize();
  const axisX = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
  const axisY = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion);
  // A Three.js camera looks down local -Z, which is also the direction behind
  // the selected wall from the user's viewpoint.
  const axisZ = new THREE.Vector3(0, 0, -1).applyQuaternion(quaternion);
  const totalDepth = depth + frontPadding;
  const center = new THREE.Vector3(bounds.center.x, bounds.center.y, bounds.center.z)
    .addScaledVector(axisZ, (depth - frontPadding) * 0.5);

  return {
    center: { x: center.x, y: center.y, z: center.z },
    halfSize: {
      x: Math.max(bounds.size.x * 0.5, 0.05),
      y: Math.max(bounds.size.y * 0.5, 0.05),
      z: totalDepth * 0.5,
    },
    axisX: { x: axisX.x, y: axisX.y, z: axisX.z },
    axisY: { x: axisY.x, y: axisY.y, z: axisY.z },
    axisZ: { x: axisZ.x, y: axisZ.y, z: axisZ.z },
  };
}

/** Returns the four world-space corners of an oriented selection's front face. */
export function getOrientedSelectionWorldCorners(
  bounds: OrientedSelectionBounds,
): WorldPos[] {
  const quaternion = new THREE.Quaternion(...bounds.quaternion).normalize();
  const center = new THREE.Vector3(bounds.center.x, bounds.center.y, bounds.center.z);
  const halfWidth = bounds.size.x * 0.5;
  const halfHeight = bounds.size.y * 0.5;

  return [
    new THREE.Vector3(-halfWidth, -halfHeight, 0),
    new THREE.Vector3(halfWidth, -halfHeight, 0),
    new THREE.Vector3(halfWidth, halfHeight, 0),
    new THREE.Vector3(-halfWidth, halfHeight, 0),
  ].map(point => {
    point.applyQuaternion(quaternion).add(center);
    return { x: point.x, y: point.y, z: point.z };
  });
}

export function vector3D(a: WorldPos, b: WorldPos): WorldVector3D {
  return {
    x: b.x - a.x,
    y: b.y - a.y,
    z: b.z - a.z,
  };
}

export function vector3DLength(v: WorldVector3D): number {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}

export function normalize3D(v: WorldVector3D): WorldVector3D | null {
  const len = vector3DLength(v);
  if (len < EPS) return null;

  return {
    x: v.x / len,
    y: v.y / len,
    z: v.z / len,
  };
}

export function dot3D(a: WorldVector3D, b: WorldVector3D): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function clampWorldXZToCircle(
  point: WorldPos,
  center: Pick<WorldPos, 'x' | 'z'>,
  radius: number,
): WorldPos {
  const dx = point.x - center.x;
  const dz = point.z - center.z;
  const distance = Math.hypot(dx, dz);
  if (distance <= radius || distance === 0) return { ...point };
  const scale = radius / distance;
  return {
    x: center.x + dx * scale,
    y: point.y,
    z: center.z + dz * scale,
  };
}

export function isPointInsideBBox(point: WorldPos, bbox: BBox3D): boolean {
  return (
    point.x >= bbox.min[0] &&
    point.x <= bbox.max[0] &&
    point.y >= bbox.min[1] &&
    point.y <= bbox.max[1] &&
    point.z >= bbox.min[2] &&
    point.z <= bbox.max[2]
  );
}

export function distancePointToBBox(point: WorldPos, bbox: BBox3D): number {
  const dx =
    point.x < bbox.min[0]
      ? bbox.min[0] - point.x
      : point.x > bbox.max[0]
      ? point.x - bbox.max[0]
      : 0;

  const dy =
    point.y < bbox.min[1]
      ? bbox.min[1] - point.y
      : point.y > bbox.max[1]
      ? point.y - bbox.max[1]
      : 0;

  const dz =
    point.z < bbox.min[2]
      ? bbox.min[2] - point.z
      : point.z > bbox.max[2]
      ? point.z - bbox.max[2]
      : 0;

  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/* ------------------------------------------------------------------ */
/* Coordinate system conversion (Z-up → Y-up)                          */
/* ------------------------------------------------------------------ */

const ZUP_TO_YUP_QUAT = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);

export function convertZUpToYUp(pos: WorldPos): WorldPos {
  const v = new THREE.Vector3(pos.x, pos.y, pos.z);
  v.applyQuaternion(ZUP_TO_YUP_QUAT);

  return {
    x: v.x,
    y: v.y,
    z: v.z,
  };
}

export function convertVectorZUpToYUp(v: WorldVector3D): WorldVector3D {
  const vec = new THREE.Vector3(v.x, v.y, v.z);
  vec.applyQuaternion(ZUP_TO_YUP_QUAT);

  return {
    x: vec.x,
    y: vec.y,
    z: vec.z,
  };
}

/** Yugo qvec/tvec are world-to-camera extrinsics, not a camera position.
 * Invert them before applying the same raw-to-viewer rotation as the splats.
 * Camera convention is OpenCV (+Z forward, -Y up); qvec is [w,x,y,z]. */
export function pinObserverToWorldView(observer: PinObserver): {
  position: [number, number, number]; lookAt: [number, number, number]; up: [number, number, number];
} {
  const [w, x, y, z] = observer.rotation;
  const inverseRotation = new THREE.Quaternion(x, y, z, w).normalize().invert();
  const center = new THREE.Vector3(...observer.translation).negate().applyQuaternion(inverseRotation);
  const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(inverseRotation);
  const up = new THREE.Vector3(0, -1, 0).applyQuaternion(inverseRotation);
  center.applyQuaternion(ZUP_TO_YUP_QUAT);
  forward.applyQuaternion(ZUP_TO_YUP_QUAT);
  up.applyQuaternion(ZUP_TO_YUP_QUAT);
  return { position: center.toArray() as [number, number, number],
    lookAt: center.clone().add(forward).toArray() as [number, number, number],
    up: up.toArray() as [number, number, number] };
}

/** Fit a pin group from its stadium-center-facing side, looking down at 45 degrees.
 * Inputs already use viewer world coordinates, exactly like the rendered pin markers. */
export function frameScenePinGroup(
  positions: WorldPos[], allPositions: WorldPos[], camera: THREE.PerspectiveCamera,
): ReturnType<typeof pinObserverToWorldView> | null {
  if (!positions.length || !allPositions.length) return null;
  const bounds = new THREE.Box3().setFromPoints(positions.map(p => new THREE.Vector3(p.x, p.y, p.z)));
  const target = bounds.getCenter(new THREE.Vector3());
  const center = allPositions.reduce((sum, p) => sum.add(new THREE.Vector3(p.x, p.y, p.z)), new THREE.Vector3())
    .divideScalar(allPositions.length);
  const outward = center.sub(target).setY(0);
  if (outward.lengthSq() < 1e-12) outward.set(0, 0, 1);
  outward.normalize().setY(1).normalize();
  const forward = outward.clone().negate();
  const right = forward.clone().cross(new THREE.Vector3(0, 1, 0)).normalize();
  const up = right.clone().cross(forward).normalize();
  // Reserve 15% on each screen half for marker size and comfortable framing.
  const tanY = Math.tan(THREE.MathUtils.degToRad(camera.getEffectiveFOV()) / 2) * 0.85;
  const tanX = tanY * camera.aspect;
  let distance = 1;
  for (const x of [bounds.min.x, bounds.max.x]) {
    for (const y of [bounds.min.y, bounds.max.y]) {
      for (const z of [bounds.min.z, bounds.max.z]) {
        const offset = new THREE.Vector3(x, y, z).sub(target);
        const depth = offset.dot(forward);
        distance = Math.max(distance, Math.abs(offset.dot(right)) / tanX - depth,
          Math.abs(offset.dot(up)) / tanY - depth, camera.near * 2 - depth);
      }
    }
  }
  return {
    position: target.clone().addScaledVector(outward, distance).toArray() as [number, number, number],
    lookAt: target.toArray() as [number, number, number],
    up: [0, 1, 0],
  };
}

/** Screen-space picking for constant-pixel-size pin markers. No per-frame DOM nodes. */
export function pickScenePin(
  positions: WorldPos[], camera: THREE.Camera, rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  clientX: number, clientY: number, radius: number,
): number | null {
  const projected = new THREE.Vector3();
  let nearest: number | null = null;
  let nearestDepth = Infinity;
  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    projected.set(p.x, p.y, p.z).project(camera);
    if (projected.z < -1 || projected.z > 1) continue;
    const dx = rect.left + (projected.x + 1) * rect.width / 2 - clientX;
    const dy = rect.top + (1 - projected.y) * rect.height / 2 - clientY;
    if (dx * dx + dy * dy <= radius * radius && projected.z < nearestDepth) {
      nearest = i;
      nearestDepth = projected.z;
    }
  }
  return nearest;
}

/** Camera-relative ground translation; vertical position and lens stay unchanged. */
export function moveCameraOnGroundPlane(camera: THREE.Camera, right: number, forward: number): void {
  const direction = camera.getWorldDirection(new THREE.Vector3());
  direction.y = 0;
  if (direction.lengthSq() < 0.000001) direction.set(0, 1, 0).applyQuaternion(camera.quaternion).setY(0);
  direction.normalize();
  const sideways = new THREE.Vector3().crossVectors(direction, camera.up).normalize();
  camera.position.addScaledVector(direction, forward).addScaledVector(sideways, right);
  camera.updateMatrixWorld(true);
}

/** Drag around a fixed house target; keep above the ground and away from the poles. */
export function orbitCameraAroundHouse(camera: THREE.Camera, center: [number, number, number], yaw: number, pitch: number): void {
  const target = new THREE.Vector3(...center);
  const spherical = new THREE.Spherical().setFromVector3(camera.position.clone().sub(target));
  spherical.theta -= yaw;
  spherical.phi = THREE.MathUtils.clamp(spherical.phi - pitch, 0.08, Math.PI / 2 - 0.05);
  camera.position.copy(target).add(new THREE.Vector3().setFromSpherical(spherical));
  camera.lookAt(target);
  camera.updateMatrixWorld(true);
}

/** Zoom around the house without passing through it or changing lens field of view. */
export function zoomCameraAboutHouse(camera: THREE.Camera, center: [number, number, number], amount: number): void {
  const target = new THREE.Vector3(...center);
  const offset = camera.position.clone().sub(target);
  offset.setLength(THREE.MathUtils.clamp(offset.length() * Math.exp(-amount), 5, Math.max(180, offset.length())));
  camera.position.copy(target).add(offset);
  camera.lookAt(target);
  camera.updateMatrixWorld(true);
}

/** Dolly along the current look direction without changing the lens or tilt. */
export function dollyCameraAlongView(camera: THREE.Camera, distance: number): void {
  const direction = camera.getWorldDirection(new THREE.Vector3());
  camera.position.addScaledVector(direction, distance);
  camera.updateMatrixWorld(true);
}

/** Apply a shared alignment offset to world-space geometry without changing its shape. */
export function translateWorldCoordinates(
  points: [number, number, number][], offset: [number, number, number],
): [number, number, number][] {
  const translation = new THREE.Vector3(...offset);
  return points.map((point) => new THREE.Vector3(...point).add(translation).toArray() as [number, number, number]);
}

/** Center plus inset corners: sample the opening without tracing its frame edges. */
export function privacyWindowSamples(corners: [number, number, number][]) {
  const points = corners.map(point => new THREE.Vector3(...point));
  const center = points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).divideScalar(points.length);
  return [center, ...points.map(point => center.clone().lerp(point, 0.65))];
}

/** Rounded window-center separation, expressed in feet for the property insights UI. */
export function privacyWindowDistanceLabel(pairs: Array<{ source: { corners: [number, number, number][] }; target: { corners: [number, number, number][] } }>) {
  if (!pairs.length) return 'Distance unavailable';
  const feet = pairs.map(pair => privacyWindowSamples(pair.source.corners)[0]
    .distanceTo(privacyWindowSamples(pair.target.corners)[0]) / 0.3048);
  const minimum = Math.round(Math.min(...feet));
  const maximum = Math.round(Math.max(...feet));
  return minimum === maximum ? `${minimum} ft` : `${minimum}–${maximum} ft`;
}

/** A translucent connector volume, not an assumed field of view or visibility result. */
export function privacySightlineFan(origin: THREE.Vector3, corners: [number, number, number][]) {
  const positions: number[] = [];
  corners.forEach((corner, index) => positions.push(...origin.toArray(), ...corner, ...corners[(index + 1) % corners.length]));
  return new Float32Array(positions);
}

/** Prefer the checked center ray; otherwise show a ray that actually tested open. */
export function privacyClearSamplePath(
  sourceCorners: [number, number, number][], targetCorners: [number, number, number][],
  samples: Array<{ visible: boolean } | null>,
) {
  const index = samples.findIndex(sample => sample?.visible === true);
  if (index < 0) return null;
  const sources = privacyWindowSamples(sourceCorners);
  const targets = privacyWindowSamples(targetCorners);
  const from = sources[Math.floor(index / targets.length)];
  const to = targets[index % targets.length];
  if (!from || !to) return null;
  return { from, to };
}

/** Soft visual beam to a measured opening; partial paths use a small patch around the checked ray. */
export function privacyBeamGeometry(from: THREE.Vector3, to: THREE.Vector3, corners: [number, number, number][], fullyClear: boolean) {
  const patch = corners.map(corner => to.clone().lerp(new THREE.Vector3(...corner), fullyClear ? 0.9 : 0.12)
    .toArray() as [number, number, number]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(privacySightlineFan(from, patch), 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(patch.flatMap(() => [0.5, 0, 0, 1, 1, 1]), 2));
  return geometry;
}

export function privacyWindowsCameraView(source: [number, number, number][], targets: [number, number, number][][], aspect = 1.2, leftInset = 0) {
  const points = [...source, ...targets.flat()].map(point => new THREE.Vector3(...point));
  const bounds = new THREE.Box3().setFromPoints(points);
  const center = bounds.getCenter(new THREE.Vector3());
  const inset = THREE.MathUtils.clamp(leftInset, 0, 0.45);
  const scale = Math.max(1, 1.2 / Math.max(aspect * (1 - inset), 0.5));
  const offset = new THREE.Vector3(5, 27, 18).multiplyScalar(scale);
  const backward = offset.clone().normalize();
  const right = backward.clone().negate().cross(new THREE.Vector3(0, 1, 0)).normalize();
  const up = backward.clone().cross(right).normalize();
  const tanY = Math.tan(THREE.MathUtils.degToRad(30)), tanX = tanY * aspect;
  let distance = offset.length();
  for (const point of points) {
    const delta = point.clone().sub(center);
    distance = Math.max(distance, delta.dot(backward) + Math.abs(delta.dot(up)) / (tanY * 0.85),
      delta.dot(backward) + Math.abs(delta.dot(right)) / (tanX * (1 - inset) * 0.8));
  }
  offset.setLength(distance);
  center.addScaledVector(right, -distance * tanX * inset);
  return { position: center.clone().add(offset).toArray(), lookAt: center.toArray() };
}

export function privacyWindowLabelPosition(corners: [number, number, number][]) {
  return privacyWindowSamples(corners)[0].add(new THREE.Vector3(0, 0.9, 0));
}

/** Keep the selected opening identifiable when the roof-height view puts it below frame. */
export function privacyWindowScreenLabel(anchor: THREE.Vector3, camera: THREE.Camera, canvas: HTMLCanvasElement) {
  const point = projectToScreen(anchor, camera, canvas);
  if (point.visible) return { ...point, offscreen: false, arrow: '' };
  const behind = anchor.clone().sub(camera.position).dot(camera.getWorldDirection(new THREE.Vector3())) <= 0;
  const x = behind ? canvas.clientWidth / 2 : point.x;
  const y = behind ? canvas.clientHeight : point.y;
  return {
    x: THREE.MathUtils.clamp(x, Math.min(320, canvas.clientWidth / 2), Math.max(canvas.clientWidth / 2, canvas.clientWidth - 70)),
    y: THREE.MathUtils.clamp(y, 42, Math.max(42, canvas.clientHeight - 100)),
    visible: true, offscreen: true,
    arrow: y >= canvas.clientHeight ? '↓' : y <= 0 ? '↑' : x < 0 ? '←' : '→',
  };
}

export function privacySurfaceObserver(source: [number, number, number][], house: [number, number, number][], height: number) {
  const bounds = new THREE.Box3().setFromPoints(house.map(point => new THREE.Vector3(...point)));
  bounds.max.y += height;
  const center = bounds.getCenter(new THREE.Vector3());
  const observer = privacyWindowSamples(source)[0];
  observer.addScaledVector(center.clone().sub(observer).setY(0).normalize(), 0.45);
  return { observer, direction: center.clone().sub(observer).normalize() };
}

/** Subdivide exterior walls into receiver patches for the schematic visibility cast. */
export function privacyHouseReceiverPanels(ring: [number, number, number][], height: number) {
  const panels: [number, number, number][][] = [];
  ring.forEach((point, index) => {
    const a = new THREE.Vector3(...point), b = new THREE.Vector3(...ring[(index + 1) % ring.length]);
    const length = a.distanceTo(b);
    if (length < 0.01) return;
    const columns = Math.ceil(length / 2.5), rows = Math.ceil(height / 2.5);
    for (let column = 0; column < columns; column++) for (let row = 0; row < rows; row++) {
      const left = a.clone().lerp(b, column / columns), right = a.clone().lerp(b, (column + 1) / columns);
      panels.push([
        left.clone().add(new THREE.Vector3(0, row * height / rows, 0)).toArray(),
        right.clone().add(new THREE.Vector3(0, row * height / rows, 0)).toArray(),
        right.clone().add(new THREE.Vector3(0, (row + 1) * height / rows, 0)).toArray(),
        left.clone().add(new THREE.Vector3(0, (row + 1) * height / rows, 0)).toArray(),
      ] as [number, number, number][]);
    }
  });
  return panels;
}

/** Apply the same parcel alignment used by the parcel overlay and tile. */
export function privacyParcelRing(ring: [number, number, number][], offset: [number, number, number]) {
  return ring.map(point => new THREE.Vector3(...point).add(new THREE.Vector3(...offset)).toArray() as [number, number, number]);
}

export function privacyInsideParcel(point: [number, number, number], ring: [number, number, number][]) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a[2] > point[2]) !== (b[2] > point[2]) && point[0] < (b[0] - a[0]) * (point[2] - a[2]) / (b[2] - a[2]) + a[0]) inside = !inside;
  }
  return inside;
}

/** Actual DSM receiver patches include roofs, vegetation, deck and yard, clipped to the lot. */
export function privacyParcelReceiverPanels(ring: [number, number, number][], grid: {
  width: number; height: number; terrainY: number[]; heights: number[]; verticalScale: number;
  corners: Record<'northwest' | 'northeast' | 'southwest' | 'southeast', [number, number, number]>;
}) {
  const { width, height, corners } = grid;
  const nw = new THREE.Vector3(...corners.northwest), ne = new THREE.Vector3(...corners.northeast);
  const sw = new THREE.Vector3(...corners.southwest), se = new THREE.Vector3(...corners.southeast);
  const sample = (column: number, row: number): [number, number, number] => {
    const index = row * width + column;
    const point = nw.clone().lerp(ne, column / (width - 1)).lerp(sw.clone().lerp(se, column / (width - 1)), row / (height - 1));
    point.y = grid.terrainY[index] + Math.max(0, grid.heights[index]) * grid.verticalScale;
    return point.toArray();
  };
  const panels: [number, number, number][][] = [];
  for (let row = 0; row < height - 2; row += 2) for (let column = 0; column < width - 2; column += 2) {
    const panel = [sample(column, row), sample(column + 2, row), sample(column + 2, row + 2), sample(column, row + 2)];
    if (panel.every(point => point.every(Number.isFinite) && privacyInsideParcel(point, ring))) panels.push(panel);
  }
  return panels;
}

/** Resolve a blocked DSM sample to the first scanned surface along that ray, within the parcel. */
export function privacyScanReceiver(observer: THREE.Vector3, target: THREE.Vector3, horizonDistance: number, parcel: [number, number, number][]) {
  if (!Number.isFinite(horizonDistance) || horizonDistance <= 0.5) return null;
  const delta = target.clone().sub(observer);
  if (horizonDistance > delta.length() + 0.5) return null;
  const point = observer.clone().addScaledVector(delta.normalize(), horizonDistance);
  return privacyInsideParcel(point.toArray(), parcel) ? point : null;
}

/** Closed 3D envelope, including near yard and elevated receivers rather than just a far wall. */
export function privacySurfaceCastGeometry(observer: THREE.Vector3, panels: [number, number, number][][], visible: (point: THREE.Vector3) => boolean,
  resolveSurface?: (point: THREE.Vector3) => THREE.Vector3 | null) {
  const receivers: THREE.Vector3[] = [];
  for (const panel of panels) for (const sample of privacyWindowSamples(panel)) {
    const point = visible(sample) ? sample : resolveSurface?.(sample);
    if (point) receivers.push(point);
  }
  if (receivers.length < 3) {
    const empty = new THREE.BufferGeometry();
    empty.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
    empty.setAttribute('uv', new THREE.Float32BufferAttribute([], 2));
    return empty;
  }
  return privacyVolumeGeometry([observer, ...receivers]);
}

/** Filled sightline volume bounded by one measured observer window and one receiver window. */
export function privacyWindowPairCastGeometry(source: [number, number, number][], target: [number, number, number][]) {
  return privacyVolumeGeometry([...source, ...target].map(point => new THREE.Vector3(...point)));
}

/** Merge the opening volumes into a single overview envelope. */
export function privacyCombinedCastGeometry(geometries: THREE.BufferGeometry[]) {
  const points: THREE.Vector3[] = [];
  for (const geometry of geometries) {
    const positions = geometry.getAttribute('position');
    for (let index = 0; index < positions.count; index++) points.push(new THREE.Vector3().fromBufferAttribute(positions, index));
  }
  return privacyVolumeGeometry(points);
}

function privacyVolumeGeometry(points: THREE.Vector3[]) {
  if (points.length < 4) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
    return geometry;
  }
  const geometry = new ConvexGeometry(points);
  const positions = geometry.getAttribute('position');
  const planes: THREE.Vector4[] = [];
  for (let index = 0; index < positions.count; index += 3) {
    // Use the hull's original normals: recomputing tiny faces from large world Float32
    // coordinates can flip their planes and incorrectly clip the whole volume away.
    const normal = new THREE.Vector3().fromBufferAttribute(geometry.getAttribute('normal'), index).normalize();
    const vertex = new THREE.Vector3().fromBufferAttribute(positions, index);
    const plane = new THREE.Plane(normal, -normal.dot(vertex) - 0.002);
    if (plane.normal.lengthSq() < 0.5) continue;
    if (!planes.some(other => Math.abs(other.x - plane.normal.x) + Math.abs(other.y - plane.normal.y) + Math.abs(other.z - plane.normal.z) < 0.0001 && Math.abs(other.w - plane.constant) < 0.01)) {
      planes.push(new THREE.Vector4(plane.normal.x, plane.normal.y, plane.normal.z, plane.constant));
    }
  }
  geometry.userData.privacyPlanes = planes;
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(positions.count * 2), 2));
  return geometry;
}

export interface PrivacyOpeningSummary {
  distanceFeet: number;
  distanceStart: THREE.Vector3;
  distanceEnd: THREE.Vector3;
  visiblePercent: number | null;
  coveragePercent: number;
  obstructions: string[];
}

type PrivacyHeightGrid = {
  width: number; height: number; terrainY: number[]; heights: number[];
  corners: Record<'northwest' | 'northeast' | 'southwest' | 'southeast', [number, number, number]>;
};

/** Approximate vegetation from elevated DSM returns outside mapped structures; never assert a tree classification. */
function privacyObstructionLabel(hit: THREE.Vector3, buildings: [number, number, number][][], grid: PrivacyHeightGrid) {
  if (buildings.some(ring => privacyInsideParcel(hit.toArray(), ring))) return 'Building or structure in path';
  const nw = grid.corners.northwest, ne = grid.corners.northeast, sw = grid.corners.southwest;
  const ax = ne[0] - nw[0], az = ne[2] - nw[2], bx = sw[0] - nw[0], bz = sw[2] - nw[2];
  const dx = hit.x - nw[0], dz = hit.z - nw[2], determinant = ax * bz - az * bx;
  const u = (dx * bz - dz * bx) / determinant, v = (ax * dz - az * dx) / determinant;
  if (u >= 0 && u <= 1 && v >= 0 && v <= 1) {
    const index = Math.round(v * (grid.height - 1)) * grid.width + Math.round(u * (grid.width - 1));
    if (grid.heights[index] > 2 && hit.y - grid.terrainY[index] > 1.5) return 'Possible tree or hedge in path';
  }
  return 'Obstruction detected in scan';
}

/** Area-weighted visibility of neighbor-facing exterior walls, rather than windows or the entire house. */
export function privacyOpeningSummary(observer: THREE.Vector3, house: [number, number, number][], height: number,
  sample: (point: THREE.Vector3) => { visible: boolean; horizonDistance: number },
  buildings: [number, number, number][][], grid: PrivacyHeightGrid): PrivacyOpeningSummary {
  const center = new THREE.Box3().setFromPoints(house.map(point => new THREE.Vector3(...point))).getCenter(new THREE.Vector3());
  const panels = privacyHouseReceiverPanels(house, height);
  let total = 0, evaluated = 0, clear = 0, nearest = Infinity;
  const distanceEnd = observer.clone();
  const obstructions = new Set<string>();
  for (const panel of panels) {
    const vertices = panel.map(point => new THREE.Vector3(...point));
    const middle = privacyWindowSamples(panel)[0];
    const normal = vertices[1].clone().sub(vertices[0]).cross(vertices[3].clone().sub(vertices[0])).normalize();
    if (normal.dot(middle.clone().sub(center)) < 0) normal.negate();
    if (normal.dot(observer.clone().sub(middle)) <= 0) continue;
    const area = vertices[0].distanceTo(vertices[1]) * vertices[0].distanceTo(vertices[3]);
    for (const triangle of [new THREE.Triangle(vertices[0], vertices[1], vertices[2]), new THREE.Triangle(vertices[0], vertices[2], vertices[3])]) {
      const closest = triangle.closestPointToPoint(observer, new THREE.Vector3());
      const distance = closest.distanceTo(observer);
      if (distance < nearest) { nearest = distance; distanceEnd.copy(closest); }
    }
    for (const point of privacyWindowSamples(panel)) {
      const weight = area / 5, result = sample(point);
      total += weight;
      if (result.visible) { evaluated += weight; clear += weight; }
      else if (Number.isFinite(result.horizonDistance) && result.horizonDistance > 0 && result.horizonDistance < point.distanceTo(observer) - 1) {
        evaluated += weight;
        const hit = observer.clone().addScaledVector(point.clone().sub(observer).normalize(), result.horizonDistance);
        obstructions.add(privacyObstructionLabel(hit, buildings, grid));
      }
    }
  }
  return { distanceFeet: nearest * 3.280839895, distanceStart: observer.clone(), distanceEnd, visiblePercent: evaluated > 0 ? Math.round(clear / total * 100) : null,
    coveragePercent: total > 0 ? Math.round(evaluated / total * 100) : 0, obstructions: [...obstructions] };
}

export function privacyDistanceScreen(summary: PrivacyOpeningSummary, camera: THREE.Camera, canvas: HTMLCanvasElement) {
  return {
    start: projectToScreen(summary.distanceStart, camera, canvas),
    end: projectToScreen(summary.distanceEnd, camera, canvas),
    label: projectToScreen(summary.distanceStart.clone().lerp(summary.distanceEnd, 0.5), camera, canvas),
  };
}

export function privacyWallDistanceScreen(start: [number, number, number], end: [number, number, number], camera: THREE.Camera, canvas: HTMLCanvasElement) {
  const from = new THREE.Vector3(...start), to = new THREE.Vector3(...end);
  return { start: projectToScreen(from, camera, canvas), end: projectToScreen(to, camera, canvas),
    label: projectToScreen(from.clone().lerp(to, 0.5), camera, canvas), distanceFeet: from.distanceTo(to) * 3.280839895 };
}

export function privacyWallDistanceFeet(start: [number, number, number], end: [number, number, number]) {
  return new THREE.Vector3(...start).distanceTo(new THREE.Vector3(...end)) * 3.280839895;
}

/** Union of visible wall samples across openings, divided by the entire house wall area. */
export function privacyCombinedHouseVisibility(observers: THREE.Vector3[], house: [number, number, number][], height: number,
  sample: (sourceIndex: number, point: THREE.Vector3) => { visible: boolean; horizonDistance: number }) {
  if (!observers.length) return { visiblePercent: null, coveragePercent: 0 };
  const center = new THREE.Box3().setFromPoints(house.map(point => new THREE.Vector3(...point))).getCenter(new THREE.Vector3());
  let total = 0, evaluated = 0, visible = 0, observed = 0;
  for (const panel of privacyHouseReceiverPanels(house, height)) {
    const vertices = panel.map(point => new THREE.Vector3(...point));
    const middle = privacyWindowSamples(panel)[0];
    const normal = vertices[1].clone().sub(vertices[0]).cross(vertices[3].clone().sub(vertices[0])).normalize();
    if (normal.dot(middle.clone().sub(center)) < 0) normal.negate();
    const facing = observers.flatMap((observer, index) => normal.dot(observer.clone().sub(middle)) > 0 ? [index] : []);
    const weight = vertices[0].distanceTo(vertices[1]) * vertices[0].distanceTo(vertices[3]) / 5;
    for (const point of privacyWindowSamples(panel)) {
      total += weight;
      if (!facing.length) { evaluated += weight; continue; }
      const results = facing.map(index => ({ index, result: sample(index, point) }));
      if (results.some(({ result }) => result.visible)) {
        visible += weight; evaluated += weight; observed += weight;
      } else if (results.every(({ index, result }) => Number.isFinite(result.horizonDistance) && result.horizonDistance > 0 && result.horizonDistance < point.distanceTo(observers[index]) - 1)) {
        evaluated += weight; observed += weight;
      }
    }
  }
  return { visiblePercent: observed > 0 && total > 0 ? Math.round(visible / total * 100) : null,
    coveragePercent: total > 0 ? Math.round(evaluated / total * 100) : 0 };
}

export function privacyFrontWindowVisibility(windows: { id: string; label: string; corners: [number, number, number][] }[],
  sample: (point: THREE.Vector3) => { visible: boolean; horizonDistance: number }, observer: THREE.Vector3,
  buildings: [number, number, number][][], grid: PrivacyHeightGrid) {
  return Object.fromEntries(windows.map(window => {
    const targets = privacyWindowSamples(window.corners);
    const results = targets.map(sample);
    const visible = results.filter(result => result.visible).length;
    const obstructions = new Set<string>();
    const blockerPoints: [number, number, number][] = [];
    let blocked = 0;
    results.forEach((result, index) => {
      const distance = observer.distanceTo(targets[index]);
      if (result.visible) return;
      if (!Number.isFinite(result.horizonDistance) || result.horizonDistance <= 0.5 || result.horizonDistance >= distance - 0.5) return;
      blocked++;
      const hit = observer.clone().addScaledVector(targets[index].clone().sub(observer).normalize(), result.horizonDistance);
      obstructions.add(privacyObstructionLabel(hit, buildings, grid));
      blockerPoints.push(hit.toArray());
    });
    const evaluated = visible + blocked;
    return [window.id, { label: window.label, visible, evaluated, total: results.length,
      percent: evaluated ? Math.round(visible / results.length * 100) : null,
      status: visible > 0 ? (blocked ? 'Partly clear' : 'Clear path found') : blocked ? 'Blocked in scan' : 'No clear path found',
      obstructions: [...obstructions], blockerPoints }];
  }));
}

/** World-space footprint clip used by the exterior visibility shader. */
export function privacyHouseClipShader(ring: [number, number, number][], height: number | null) {
  const bottom = Math.min(...ring.map(point => point[1])) - 0.3;
  const top = Math.max(...ring.map(point => point[1])) + (height ?? 0) + 1.5;
  const points = ring.map(point => `vec2(${point[0].toFixed(6)}, ${point[2].toFixed(6)})`).join(', ');
  return `bool privacyInsideHouse(vec3 p) {
    ${height === null ? '' : `if (p.y < ${bottom.toFixed(6)} || p.y > ${top.toFixed(6)}) return false;`}
    const vec2 polygon[${ring.length}] = vec2[${ring.length}](${points});
    bool inside = false;
    for (int i = 0, j = ${ring.length - 1}; i < ${ring.length}; j = i++) {
      vec2 a = polygon[i]; vec2 b = polygon[j];
      if ((a.y > p.z) != (b.y > p.z)) {
        if (p.x < (b.x - a.x) * (p.z - a.y) / (b.y - a.y) + a.x) inside = !inside;
      }
    }
    return inside;
  }`;
}

/** Fill the viewport with nearby blocks instead of shrinking the complete capture into view. */
export function neighborhoodCameraView(corners: WorldPos[], aspect = 1.2, fov = 60) {
  const bounds = new THREE.Box3().setFromPoints(corners.map(point => new THREE.Vector3(point.x, point.y, point.z)));
  const center = bounds.getCenter(new THREE.Vector3()).add(new THREE.Vector3(-25, 8, 4));
  const size = bounds.getSize(new THREE.Vector3());
  const direction = new THREE.Vector3(0.45, 1, -0.16).normalize();
  const tanY = Math.tan(THREE.MathUtils.degToRad(fov / 2));
  const tanX = tanY * Math.max(aspect, 0.4);
  const distance = Math.min(size.x / (2 * tanY), size.z / (2 * tanX)) * 0.58 * Math.min(1, aspect / 1.3);
  return { position: center.clone().addScaledVector(direction, distance).toArray(), lookAt: center.toArray() };
}

/** Stay above the roof edge instead of descending into blurry ground-level splats. */
export function createPrivacyWindowTour(
  camera: THREE.Camera,
  source: [number, number, number][],
  targets: [number, number, number][][],
  destination?: { position: [number, number, number]; lookAt: [number, number, number] },
  direct = false,
) {
  const sourceCenter = privacyWindowSamples(source)[0];
  const targetCenter = privacyWindowSamples(targets.flat())[0];
  const towardHome = targetCenter.clone().sub(sourceCenter).setY(0).normalize();
  const side = new THREE.Vector3(-towardHome.z, 0, towardHome.x);
  const finalPosition = destination ? new THREE.Vector3(...destination.position) : null;
  const finalTarget = destination ? new THREE.Vector3(...destination.lookAt) : null;
  // Approach from the street/front side, then advance over the roof toward the rear.
  // Captured neighbor viewpoints use a high cruise altitude so the tour stays
  // above the property roof instead of diving toward the measured window.
  const cruiseHeight = destination ? Math.max(camera.position.y, finalPosition!.y) : null;
  const eye = sourceCenter.clone().addScaledVector(towardHome, -14).addScaledVector(side, 2).add(new THREE.Vector3(0, 10, 0));
  const approach = sourceCenter.clone().addScaledVector(towardHome, -21).addScaledVector(side, 4).add(new THREE.Vector3(0, 13, 0));
  const around = sourceCenter.clone().addScaledVector(towardHome, -27).addScaledVector(side, 14).add(new THREE.Vector3(0, 16, 0));
  if (cruiseHeight !== null) {
    eye.y = cruiseHeight;
    approach.y = cruiseHeight;
    around.y = cruiseHeight;
  }
  const startTarget = camera.getWorldDirection(new THREE.Vector3())
    .multiplyScalar(camera.position.distanceTo(targetCenter)).add(camera.position);
  const landingPosition = finalPosition ?? eye;
  const landingTarget = finalTarget ?? targetCenter;
  const positionPath: THREE.Curve<THREE.Vector3> = direct && destination
    ? new THREE.LineCurve3(camera.position.clone(), landingPosition)
    : new THREE.CatmullRomCurve3([camera.position.clone(), around, approach, eye, landingPosition], false, 'centripetal');
  const targetPath: THREE.Curve<THREE.Vector3> = direct && destination
    ? new THREE.LineCurve3(startTarget, landingTarget)
    : new THREE.CatmullRomCurve3([startTarget, sourceCenter.clone().lerp(targetCenter, 0.5), targetCenter, landingTarget], false, 'centripetal');
  return {
    position: positionPath,
    target: targetPath,
    eye: landingPosition, targetCenter: landingTarget,
  };
}

export function updatePrivacyWindowTour(camera: THREE.Camera, tour: ReturnType<typeof createPrivacyWindowTour>, progress: number) {
  const t = THREE.MathUtils.smootherstep(progress, 0, 1);
  camera.position.copy(tour.position.getPoint(t));
  camera.lookAt(tour.target.getPoint(t));
  camera.updateMatrixWorld(true);
}

/** Interpolate a camera and its look-at target together for a cinematic transition. */
export function interpolateCameraPose(
  camera: THREE.Camera,
  fromPosition: [number, number, number], toPosition: [number, number, number],
  fromTarget: [number, number, number], toTarget: [number, number, number], progress: number,
): void {
  const t = THREE.MathUtils.smootherstep(progress, 0, 1);
  camera.position.lerpVectors(new THREE.Vector3(...fromPosition), new THREE.Vector3(...toPosition), t);
  camera.lookAt(new THREE.Vector3(...fromTarget).lerp(new THREE.Vector3(...toTarget), t));
  camera.updateMatrixWorld(true);
}

export interface HousePreviewTour {
  startAngle: number;
  endAngle: number;
  parameters: THREE.CatmullRomCurve3;
  center: THREE.Vector3;
  initialTarget: THREE.Vector3;
}

/** Continuous spherical route: broad neighborhood orbit, overhead orbit, close orbit. */
export function createHousePreviewTour(
  start: [number, number, number], initialTarget: [number, number, number],
): HousePreviewTour {
  const center = new THREE.Vector3(-2287.2, 742, 3773.2);
  const offset = new THREE.Vector3(...start).sub(center);
  const angle = Math.atan2(offset.z, offset.x);
  const front = Math.atan2(3771.5 - center.z, -2256 - center.x);
  // Finish facing the front after continuing in the same direction throughout.
  const finishAngle = front + Math.ceil((angle + Math.PI * 2 - front) / (Math.PI * 2)) * Math.PI * 2;
  const radius = Math.hypot(offset.x, offset.z);
  const parameters = new THREE.CatmullRomCurve3([
    new THREE.Vector3(angle, radius, offset.y),
    new THREE.Vector3(angle + 0.65, 38, 48),
    new THREE.Vector3(angle + 2.15, 38, 48),
    new THREE.Vector3(angle + 3.15, 4, 54),
    new THREE.Vector3(angle + 4.95, 4, 54),
    new THREE.Vector3(finishAngle - 1.8, 32, 30),
    new THREE.Vector3(finishAngle, 32, 34),
  ], false, 'catmullrom', 0.25);
  return { parameters, center, initialTarget: new THREE.Vector3(...initialTarget), startAngle: angle, endAngle: finishAngle };
}

export function updateHousePreviewTour(camera: THREE.Camera, tour: HousePreviewTour, progress: number): void {
  const p = THREE.MathUtils.clamp(progress, 0, 1);
  const ramp = 0.08; // Only the first/last 2.56 seconds of the 32-second tour ease speed.
  let distance: number;
  if (p < ramp) {
    const u = p / ramp;
    distance = ramp * (u ** 3 - 0.5 * u ** 4) / (1 - ramp);
  } else if (p > 1 - ramp) {
    const u = (1 - p) / ramp;
    distance = 1 - ramp * (u ** 3 - 0.5 * u ** 4) / (1 - ramp);
  } else {
    distance = (p - ramp / 2) / (1 - ramp);
  }
  const angle = THREE.MathUtils.lerp(tour.startAngle, tour.endAngle, distance);
  // Invert the curve's angle so uneven waypoint spacing cannot speed up the spin.
  let low = 0, high = 1;
  for (let step = 0; step < 24; step += 1) {
    const middle = (low + high) / 2;
    if (tour.parameters.getPoint(middle).x < angle) low = middle;
    else high = middle;
  }
  const spherical = tour.parameters.getPoint(p === 0 ? 0 : p === 1 ? 1 : (low + high) / 2);
  const radius = Math.max(4, spherical.y);
  camera.position.set(
    tour.center.x + Math.cos(spherical.x) * radius,
    tour.center.y + spherical.z,
    tour.center.z + Math.sin(spherical.x) * radius,
  );
  const target = tour.initialTarget.clone().lerp(tour.center, THREE.MathUtils.smootherstep(progress, 0, 0.15));
  camera.lookAt(target);
  camera.updateMatrixWorld(true);
}
